import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

const SINGLE_CLICK_DELAY_MS = 220;
const HOVER_HIDE_DELAY_MS   = 150;
const LABEL_HIDE_DELAY_MS   = 100;

const usePlotlyEvents = ({
  isLabelMode,
  layout,
  annotations,
  labelsRawRef,
  sectionsRef,
  tRawRef,
  setHoveredLabel,
  timerEtiquette,
  handlePhaseClickInternal,
  onSignalClick,
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  signalCount,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [signalHover, setSignalHover] = useState(null);
  const plotElRef = useRef(null);
  const pendingSignalClickTimer = useRef(null);
  const signalHoverTimerRef = useRef(null);
  const annotationsRef = useRef(annotations);
  annotationsRef.current = annotations;

  // Map _t → index pour lookup O(1) au lieu de findIndex O(n)
  const annotationsMapRef = useRef(new Map());
  useEffect(() => {
    const map = new Map();
    annotations.forEach((a, i) => map.set(Number(a._t), i));
    annotationsMapRef.current = map;
  }, [annotations]);

  const clearPendingSignalClick = useCallback(() => {
    if (pendingSignalClickTimer.current) {
      clearTimeout(pendingSignalClickTimer.current);
      pendingSignalClickTimer.current = null;
    }
  }, []);

  const cancelSignalHoverClear = useCallback(() => {
    clearTimeout(signalHoverTimerRef.current);
  }, []);

  const clearSignalHoverDelayed = useCallback(() => {
    signalHoverTimerRef.current = setTimeout(() => setSignalHover(null), HOVER_HIDE_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      clearPendingSignalClick();
      clearTimeout(signalHoverTimerRef.current);
    },
    [clearPendingSignalClick]
  );

  const activeLayout = useMemo(() => {
    if (hoveredIndex === null) return layout;
    const ann = annotations[hoveredIndex];
    return {
      ...layout,
      annotations: [...(layout.annotations || []), ...(ann ? [ann] : [])],
    };
  }, [layout, annotations, hoveredIndex]);

  const handleLabelHoverInternal = useCallback(
    (t, x, y) => {
      clearTimeout(timerEtiquette.current);
      const tNum = Number(t);
      const all = labelsRawRef.current;
      if (!all.length) return;
      const found = all.reduce((best, l) =>
        Math.abs(Number(l.t) - tNum) < Math.abs(Number(best.t) - tNum) ? l : best
      );
      if (!found) return;
      setHoveredLabel({ label: found, x, y });
      const annIdx = annotationsMapRef.current.get(Number(found.t));
      if (annIdx !== undefined) setHoveredIndex(annIdx);
    },
    [setHoveredLabel]
  );

  const handleLabelUnhoverInternal = useCallback(() => {
    timerEtiquette.current = setTimeout(() => {
      setHoveredLabel(null);
      setHoveredIndex(null);
    }, LABEL_HIDE_DELAY_MS);
  }, [setHoveredLabel]);

  const getNearestPoint = useCallback(
    (e, signalOnly = false) => {
      const points = e?.points || [];
      if (!points.length) return null;

      const nSignal = signalCount || 1;
      const candidates = signalOnly
        ? points.filter((p) => p?.curveNumber < nSignal)
        : points;

      if (!candidates.length) return null;
      if (candidates.length === 1) return candidates[0];

      const mouseX = e?.event?.clientX;
      const mouseY = e?.event?.clientY;
      if (mouseX == null || mouseY == null) return candidates[0];

      let best = candidates[0];
      let bestDist = Number.POSITIVE_INFINITY;

      candidates.forEach((p) => {
        const xa = p?.xaxis;
        const ya = p?.yaxis;
        if (
          !xa || !ya ||
          typeof xa.d2p !== 'function' ||
          typeof ya.d2p !== 'function' ||
          xa._offset == null || ya._offset == null
        ) return;
        try {
          const px = xa._offset + xa.d2p(p.x);
          const py = ya._offset + ya.d2p(p.y);
          const d = Math.hypot(px - mouseX, py - mouseY);
          if (d < bestDist) { bestDist = d; best = p; }
        } catch (_) {}
      });

      return best;
    },
    [signalCount]
  );

  const handleHover = useCallback(
    (e) => {
      const pt = getNearestPoint(e);
      if (!pt) return;

      const nSignal = signalCount || 1;

      if (pt.curveNumber < nSignal) {
        const allPoints = e?.points || [];
        const labelPt = allPoints.find(
          (p) =>
            p.curveNumber >= nSignal &&
            p.customdata != null &&
            p.customdata?.type !== 'phase'
        );
        if (labelPt) {
          if (!plotElRef.current)
            plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
          if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
          clearTimeout(signalHoverTimerRef.current);
          setSignalHover(null);
          if (isLabelMode) {
            handleLabelHoverInternal(labelPt.customdata, e.event?.clientX, e.event?.clientY);
          } else if (onLabelHover) {
            onLabelHover(labelPt.customdata, e.event?.clientX, e.event?.clientY);
          }
          return;
        }

        if (isLabelMode) {
          clearTimeout(signalHoverTimerRef.current);
          if (!plotElRef.current)
            plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
          if (plotElRef.current) plotElRef.current.style.cursor = 'crosshair';
          const ts = pt.pointNumber != null ? tRawRef.current?.[pt.pointNumber] : null;
          const tsDate =
            ts != null
              ? new Date(ts * 1000).toLocaleString('fr-FR', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })
              : null;
          setSignalHover({
            x: e.event?.clientX,
            y: e.event?.clientY,
            ts: ts != null ? Number(ts) : null,
            yVal: pt.y,
            curveNumber: pt.curveNumber,
            formula: pt.data?.meta?.formula || null,
            signalName: pt.data?.name || null,
            tsDate,
          });
        }
        return;
      }

      if (pt.customdata !== undefined) {
        if (pt.customdata?.type === 'phase') return;
        if (!plotElRef.current)
          plotElRef.current = e.event?.target?.closest?.('.js-plotly-plot');
        if (plotElRef.current) plotElRef.current.style.cursor = 'pointer';
        if (isLabelMode) {
          handleLabelHoverInternal(pt.customdata, e.event?.clientX, e.event?.clientY);
        } else if (onLabelHover) {
          onLabelHover(pt.customdata, e.event?.clientX, e.event?.clientY);
        }
        return;
      }

      setHoveredIndex(pt.curveNumber - nSignal);
    },
    [getNearestPoint, isLabelMode, handleLabelHoverInternal, onLabelHover, signalCount]
  );

  const handleUnhover = useCallback(() => {
    if (plotElRef.current) plotElRef.current.style.cursor = '';
    signalHoverTimerRef.current = setTimeout(() => setSignalHover(null), HOVER_HIDE_DELAY_MS);
    if (isLabelMode) {
      handleLabelUnhoverInternal();
    } else {
      setHoveredIndex(null);
      if (onLabelUnhover) onLabelUnhover();
    }
  }, [isLabelMode, handleLabelUnhoverInternal, onLabelUnhover]);

  const handleClickAnnotation = useCallback(
    (e) => {
      if (e.index === undefined) return;
      const sec = sectionsRef.current[e.index];
      if (!sec) return;
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart, tEnd: sec.tEnd,
        label: sec.label, color: sec.color,
      };
      if (isLabelMode) {
        handlePhaseClickInternal(phaseData);
      } else if (onPhaseClick) {
        onPhaseClick(phaseData, e.event?.clientX, e.event?.clientY);
      }
    },
    [isLabelMode, handlePhaseClickInternal, onPhaseClick]
  );

  const handleClick = useCallback(
    (e) => {
      const clickDetail = Number(e?.event?.detail || 1);
      if (clickDetail > 1) { clearPendingSignalClick(); return; }

      const nSignal = signalCount || 1;
      const signalPt = getNearestPoint(e, true);
      if (signalPt && signalPt.curveNumber < nSignal) {
        const ts = tRawRef.current?.[signalPt.pointNumber];
        clearPendingSignalClick();
        pendingSignalClickTimer.current = setTimeout(() => {
          onSignalClick?.({
            ts: Number(ts ?? signalPt.x),
            yVal: signalPt.y,
            curveIndex: signalPt.curveNumber,
            formula: signalPt.data?.meta?.formula || null,
          });
          pendingSignalClickTimer.current = null;
        }, SINGLE_CLICK_DELAY_MS);
        return;
      }

      const pt = getNearestPoint(e);
      if (!pt) return;
      if (pt.customdata?.type !== 'phase') return;
      if (isLabelMode) {
        handlePhaseClickInternal(pt.customdata);
      } else if (onPhaseClick) {
        onPhaseClick(pt.customdata, e.event?.clientX, e.event?.clientY);
      }
    },
    [
      clearPendingSignalClick, getNearestPoint, isLabelMode,
      handlePhaseClickInternal, onSignalClick, onPhaseClick, signalCount,
    ]
  );

  const handleDoubleClick = useCallback(() => {
    clearPendingSignalClick();
  }, [clearPendingSignalClick]);

  return {
    activeLayout,
    handleHover,
    handleUnhover,
    handleClick,
    handleDoubleClick,
    handleClickAnnotation,
    signalHover,
    setSignalHover,
    cancelSignalHoverClear,
    clearSignalHoverDelayed,
  };
};

export default usePlotlyEvents;
